// قواعد رمز (BR-IAM-002، سیاست Realm phoenix-staff): حداقل ۱۰ و حداکثر ۱۲۸ نویسه، برابر نبودن با نام کاربری (موبایل) و
// نبودن در فهرست رمزهای رایج (فقط سرور/Keycloak — PHX-IAM-0303). فیلدهای کد یکبارمصرف و رمز مشترک پذیرش دعوت و بازیابی.
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, latinDigits } from '@phoenix/ui';
import { z } from 'zod';

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;
/** طول کد OTP (BR-MSG-050: ۵ رقم). */
export const OTP_LENGTH = 5;

export interface PasswordRuleState {
  id: 'length' | 'not_mobile' | 'common';
  met: boolean | null;
}

/** وضعیت قواعد قابل سنجش در Client؛ «رمز رایج» فقط در سرور (null). */
export function passwordRules(password: string, mobile?: string): PasswordRuleState[] {
  const normalizedMobile = mobile ? latinDigits(mobile) : undefined;
  return [
    {
      id: 'length',
      met: password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
    },
    {
      id: 'not_mobile',
      met: password.length > 0 && (!normalizedMobile || !password.includes(normalizedMobile)),
    },
    { id: 'common', met: null },
  ];
}

/** Schema رمز تازه و تکرار آن (پیام‌ها کلید i18n). */
export function newPasswordSchema(mobile?: string) {
  return z
    .object({
      password: z
        .string()
        .min(PASSWORD_MIN_LENGTH, { error: 'iam:password.too_short' })
        .max(PASSWORD_MAX_LENGTH, { error: 'common:validation.too_long' })
        .refine((value) => !mobile || !value.includes(latinDigits(mobile)), {
          error: 'iam:password.contains_mobile',
        }),
      confirm: z.string(),
    })
    .refine((form) => form.password === form.confirm, { path: ['confirm'], error: 'iam:password.mismatch' });
}

export const otpCodeSchema = z
  .string()
  .transform((value) => latinDigits(value).replace(/\D/g, ''))
  .pipe(z.string().length(OTP_LENGTH, { error: 'iam:otp.invalid_format' }));

/** فهرست قواعد رمز با وضعیت زنده (BR-IAM-002). */
export function PasswordRules({ password, mobile }: { password: string; mobile?: string }) {
  const { t } = useTranslation('iam');
  return (
    <List dense disablePadding aria-label={t('iam:password.rules_title')} data-testid="password-rules">
      {passwordRules(password, mobile).map((rule) => (
        <ListItem key={rule.id} disableGutters data-rule={rule.id} data-met={rule.met === true}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            {rule.met === null ? (
              <InfoOutlinedIcon fontSize="small" color="action" />
            ) : rule.met ? (
              <CheckCircleOutlineIcon fontSize="small" color="success" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={t(`iam:password.rules.${rule.id}`, {
              min: faDigits(PASSWORD_MIN_LENGTH),
            })}
          />
        </ListItem>
      ))}
    </List>
  );
}
